// A device's diagnosis from the assistant (ZENITH.md §16; ADR-0210, #1864): a
// language model's reading of this one device, drawn from what its own page
// answers, asked for by a person holding `ai.use` at the device's site and
// never on render, on a refresh or on a cadence — a call spends the
// deployment's money and sends the tenant's data to a third party (ADR-0210
// decision 5a), so it is the person's act, as the Overview's *Brief me* is.
//
// It is the briefing's sibling and takes the briefing's one provider path: the
// request through `useMutation().runWith` on the generated client, every
// refusal through `ProblemBox` in its reason's own words, and the answer as
// plain text in paragraphs (`paragraphs` in `dashboard/format.ts`, which the
// briefing reads too), never as markup.
//
// **It is not the Diagnostics tab and does not say so in its name.** That tab
// is the superuser's ADR-0092 window onto the cell, which ADR-0210 decision 3c
// refuses as the model's input, so the control is *Ask the assistant* and its
// gate is `ai.use`, not `mayDiagnose`.
//
// **It sits outside the head's acts** (`DeviceActions`): those are §11.2's
// device acts, pinned by ADR-0149 decision 2 and #1801, and a diagnosis sends
// nothing to the device. ZENITH.md §18 is silent on where it goes; it is a
// panel of its own under the Identity facts, above the tabs, so it is the same
// on every tab (#1864's merge request names the choice).
//
// **An answer belongs to one serial.** The page mounts this under
// `DeviceDetail`, keyed by serial, and keys it by serial again itself, so
// another device's page starts with nothing asked; and an answer is drawn only
// while its own `serial` is the one this panel is about.
import { useState } from "react";
import { api } from "../../api/client";
import { useMutation } from "../../api/mutation";
import type { components } from "../../api/schema";
import { paragraphs } from "../../dashboard/format";
import { Info, ProblemBox } from "../../ui";
import { CloseIcon, Glyph } from "../../icons";

type Diagnosis = components["schemas"]["Diagnosis"];

export default function DeviceDiagnosis({ serial }: { serial: string }) {
  const ask = useMutation();
  const [diagnosis, setDiagnosis] = useState<Diagnosis>();
  const run = async () => {
    setDiagnosis(undefined);
    const r = await ask.runWith(() => api.POST("/ai:diagnose", { body: { serial } }));
    if (r.ok && r.data) setDiagnosis(r.data);
  };
  const close = () => { setDiagnosis(undefined); ask.clear(); };
  const shown = diagnosis && diagnosis.serial === serial ? diagnosis : undefined;
  const asked = !!shown || !!ask.problem;
  return (
    <section className="panel" aria-label="Assistant" aria-busy={ask.busy}>
      <div className="edit-bar">
        <h2 style={{ margin: 0 }}>Assistant</h2>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn-sm" disabled={ask.busy} onClick={() => void run()}>
          {asked ? "Ask again" : "Ask the assistant"}
        </button>
        {!asked && !ask.busy && (
          // What asking sends and to whom, behind the ⓘ beside the act (#1900).
          <Info label="About the assistant">
            {/* Restates ADR-0210 decisions 3 (`3a`) and 5a — the model is given what this device's page
                shows and nothing from its statistics or events, and asking is the person's act: nothing
                is asked until this is pressed, and nothing is sent to the device. */}
            An AI model can read what this page shows about the device and say what may be wrong with it.
            Nothing is asked until you press <em>Ask the assistant</em>, and nothing is sent to the device.
          </Info>
        )}
        {asked && !ask.busy && <button type="button" className="btn btn-quiet" onClick={close} aria-label="Close the assistant's answer"><Glyph icon={CloseIcon} /></button>}
      </div>
      {ask.busy && <p className="muted" role="status">Asking the assistant about this device…</p>}
      {!ask.busy && ask.problem && <ProblemBox problem={ask.problem} />}
      {!ask.busy && !ask.problem && shown && (
        <>
          {/* Restates ADR-0210 decision 4 — a model that declines is told as that,
              and nothing is invented in its place. */}
          {shown.outcome === "declined"
            ? <p>The assistant declined to answer about this device.</p>
            : paragraphs(shown.text ?? "").map((p, i) => <p key={i}>{p}</p>)}
          {/* Restates ADR-0210 decision 3 (`3a`) — the answer is drawn from what this device's
              page shows; its statistics and recent events are not in the input yet (§16). The
              Diagnosis carries no time and no cached flag, so neither is claimed. */}
          {shown.outcome === "answered" && (
            <p className="dim small">
              Written by an AI model from what this page shows about the device. Its statistics and recent events were not part of what it was given. Check the page before acting on it.
            </p>
          )}
        </>
      )}
    </section>
  );
}
